/**
 * The four routed ways into a song, as a row of cards at the top of /create.
 *
 * WHAT THIS IS FOR. Music DNA, the Genre Mixer, Image Music and Voice Cloner each have a
 * route of their own, and /create is the screen that says they belong to one section.
 * Voice Cloner is the only one here that is intentionally teaser-shaped rather than fully
 * available, but it is still a real destination and therefore sits with the others rather
 * than in a separate announcement block.
 *
 * IT DUPLICATES NOTHING. These are links. Every method keeps its own route, its own
 * store (where it has one) and its own behaviour — there is no generation logic here and
 * no second copy of any screen. The composer underneath is still the text-to-music path
 * it always was.
 *
 * NOT NUMBERED, deliberately. They are alternatives, not steps: numbering them 1-2-3-4
 * on the screen would read as a sequence to work through. The eyebrow above says
 * "four ways", which is the fact that matters.
 *
 * PRESENTATIONAL, per the components/ rules — no store reads and no API calls. The
 * `preview` and `comingSoon` props are how the honest state of a method reaches it: the
 * route asks the generation boundary what it can actually do and passes the answer in.
 * See routes/Create.tsx.
 */
import type { LucideIcon } from 'lucide-react';
import { Dna, Image as ImageIcon, Mic2, Shuffle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PreviewTag } from '../ui';
import { paths } from '../../routes/paths';
import { c } from '../../styles/theme';

/** Which method a card is. */
export type CreateMethodKey = 'dna' | 'mixer' | 'image' | 'voice';

interface CreateMethod {
  key: CreateMethodKey;
  title: string;
  /** One line. What you start from, not what the feature is called again. */
  detail: string;
  to: string;
  icon: LucideIcon;
}

/*
  In the brief's order — Music DNA, Genre Mixer, Image Music, Voice Cloner — which is also
  the order the sidebar nests them in, so the two surfaces cannot disagree about which is
  which.

  Static presentation data rather than a prop, for the same reason COMPOSER_ICONS is a
  const in Composer.tsx: there is one set of methods and every call site would pass the
  same list.
*/
const METHODS: readonly CreateMethod[] = [
  {
    key: 'dna',
    title: 'Music DNA',
    detail: 'Start from a track you already like.',
    to: paths.dna,
    icon: Dna,
  },
  {
    key: 'mixer',
    title: 'Genre Mixer',
    detail: 'Blend two or more genres into one sound.',
    to: paths.genreMixer,
    icon: Shuffle,
  },
  {
    key: 'image',
    title: 'Image Music',
    detail: 'Turn a picture into music.',
    to: paths.imageMusic,
    icon: ImageIcon,
  },
  {
    key: 'voice',
    title: 'Voice Cloner',
    detail: 'Join the notify list for your personalised AI singing voice.',
    to: paths.voiceCloner,
    icon: Mic2,
  },
];

export interface CreateMethodsProps {
  /**
   * Methods whose generation is not yet wired to a real API, marked with the existing
   * Preview tag.
   *
   * A list rather than a boolean named after one method, so the next one to be in that
   * state does not need a second prop. Image Music is currently the only entry: its
   * screen is finished up to the request and cannot send it, which is worth knowing
   * before following the link rather than after.
   */
  preview?: readonly CreateMethodKey[];
  /**
   * Methods that are intentionally teaser destinations rather than active creation flows.
   *
   * Voice Cloner is the current entry: it is a real page with a waitlist action, but the
   * card should still say "soon" so the surface stays aligned with the sidebar's muted row.
   */
  comingSoon?: readonly CreateMethodKey[];
}

export default function CreateMethods({ preview = [], comingSoon = [] }: CreateMethodsProps) {
  return (
    <section aria-label="Ways to create" style={{ marginBottom: 20 }}>
      <p className="lyx-ws-eyebrow">Four ways to create</p>
      {/* The grid is in global.css and uses auto-fit rather than a breakpoint: four
          across when there is room, then two or one as the width narrows, with no media
          query to keep in step with the content width. */}
      <div className="lyx-create-methods">
        {METHODS.map(({ key, title, detail, to, icon: Icon }) => {
          const teaser = comingSoon.includes(key);
          return (
            <Link
              key={key}
              to={to}
              className="lyx-create-method"
              style={
                teaser
                  ? {
                      backgroundColor: c.surface2,
                      boxShadow: `0 0 0 1px ${c.border} inset`,
                    }
                  : undefined
              }
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icon size={15} color={teaser ? c.text3 : c.accent} aria-hidden="true" />
                <span
                  style={{
                    fontFamily: 'Space Grotesk',
                    fontWeight: 600,
                    fontSize: 13,
                    color: teaser ? c.text2 : c.text1,
                    flex: 1,
                  }}
                >
                  {title}
                </span>
                {teaser ? <PreviewTag label="Soon" /> : preview.includes(key) ? <PreviewTag /> : null}
              </span>
              <span style={{ fontSize: 11.5, color: teaser ? c.text3 : c.text2, lineHeight: 1.5 }}>{detail}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
